import React, { useState } from 'react';
import './TestPanel.css';

const TestPanel = () => {
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);

  const addTestResult = (testName, status, message) => {
    setTestResults(prev => [...prev, {
      id: `${Date.now()}-${Math.random()}`,
      name: testName,
      status,
      message,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const testCSVLoading = async () => {
    addTestResult('Загрузка CSV', 'running', 'Проверка доступности файла...');
    
    try {
      const response = await fetch('/matrica.csv');
      if (response.ok) {
        const csvText = await response.text();
        const lines = csvText.split('\n').filter(line => line.trim());
        addTestResult('Загрузка CSV', 'success', `Загружено ${lines.length} строк`);
        return csvText;
      } else {
        addTestResult('Загрузка CSV', 'error', 'Файл недоступен');
        return null;
      }
    } catch (error) {
      addTestResult('Загрузка CSV', 'error', `Ошибка: ${error.message}`);
      return null;
    }
  };

  const testDataParsing = (csvText) => {
    addTestResult('Парсинг данных', 'running', 'Анализ структуры данных...');
    
    if (!csvText) {
      addTestResult('Парсинг данных', 'error', 'Нет данных для анализа');
      return;
    }

    const lines = csvText.split('\n').filter(line => line.trim());
    const parsedData = [];
    
    lines.forEach(line => {
      const columns = line.split(';');
      if (columns.length >= 4) {
        parsedData.push({
          role: columns[0],
          action: columns[1],
          method: columns[2],
          type: columns[3]
        });
      }
    });

    const roles = [...new Set(parsedData.map(item => item.role))];
    const actions = [...new Set(parsedData.map(item => item.action))];
    const methods = [...new Set(parsedData.map(item => item.method))];

    addTestResult('Парсинг данных', 'success', 
      `${parsedData.length} записей, ${roles.length} ролей, ${actions.length} действий, ${methods.length} методов`);
  };

  const testThemeSwitching = () => {
    addTestResult('Переключение тем', 'running', 'Проверка функциональности тем...');
    
    const mainApp = document.querySelector('.main-app');
    if (!mainApp) {
      addTestResult('Переключение тем', 'error', 'Элемент .main-app не найден');
      return;
    }

    // Тестируем переключение на темную тему
    mainApp.className = 'main-app dark-theme';
    const hasDarkTheme = mainApp.classList.contains('dark-theme');
    
    if (hasDarkTheme) {
      // Возвращаем к светлой теме
      mainApp.className = 'main-app';
      addTestResult('Переключение тем', 'success', 'Темы переключаются корректно');
    } else {
      addTestResult('Переключение тем', 'error', 'Не удалось применить темную тему');
    }
  };

  const testNavigation = () => {
    addTestResult('Навигация', 'running', 'Проверка кнопок навигации...');
    
    const navButtons = document.querySelectorAll('.nav-btn');
    if (navButtons.length === 0) {
      addTestResult('Навигация', 'error', 'Кнопки навигации не найдены');
      return;
    }

    const buttonTexts = Array.from(navButtons).map(btn => btn.textContent.trim());
    addTestResult('Навигация', 'success', `Найдено ${navButtons.length} кнопок: ${buttonTexts.join(', ')}`);
  };

  const testComponents = () => {
    addTestResult('Компоненты', 'running', 'Проверка наличия компонентов...');
    
    const components = [
      { name: 'GreetingScreen', selector: '.greeting-screen' },
      { name: 'MatricParser', selector: '.parser-container' },
      { name: 'AnalyticsDashboard', selector: '.analytics-dashboard' },
      { name: 'SettingsPanel', selector: '.settings-panel' }
    ];

    const foundComponents = [];
    components.forEach(component => {
      const element = document.querySelector(component.selector);
      if (element) {
        foundComponents.push(component.name);
      }
    });

    if (foundComponents.length > 0) {
      addTestResult('Компоненты', 'success', `Найдены: ${foundComponents.join(', ')}`);
    } else {
      addTestResult('Компоненты', 'warning', 'Компоненты не найдены (возможно, не отображаются)');
    }
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    
    addTestResult('Тестирование', 'info', 'Запуск тестов приложения...');
    
    // Запускаем тесты последовательно
    const csvText = await testCSVLoading();
    testDataParsing(csvText);
    testThemeSwitching();
    testNavigation();
    testComponents();
    
    addTestResult('Тестирование', 'success', 'Все тесты завершены');
    setIsRunning(false);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'running': return '🔄';
      case 'info': return 'ℹ️';
      default: return '❓';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'success': return 'test-success';
      case 'error': return 'test-error';
      case 'warning': return 'test-warning';
      case 'running': return 'test-running';
      case 'info': return 'test-info';
      default: return '';
    }
  };

  return (
    <div className="test-panel">
      <div className="test-header">
        <h2>🧪 Панель тестирования</h2>
        <div className="test-controls">
          <button 
            className="btn btn-primary"
            onClick={runAllTests}
            disabled={isRunning}
          >
            {isRunning ? '🔄 Тестирование...' : '🚀 Запустить тесты'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={clearResults}
            disabled={isRunning}
          >
            🗑️ Очистить
          </button>
        </div>
      </div>

      <div className="test-results">
        {testResults.length === 0 ? (
          <div className="no-results">
            <p>Нажмите "Запустить тесты" для начала тестирования</p>
          </div>
        ) : (
          testResults.map(result => (
            <div key={result.id} className={`test-result ${getStatusClass(result.status)}`}>
              <div className="test-result-header">
                <span className="test-icon">{getStatusIcon(result.status)}</span>
                <span className="test-name">{result.name}</span>
                <span className="test-time">{result.timestamp}</span>
              </div>
              <div className="test-message">{result.message}</div>
            </div>
          ))
        )}
      </div>

      <div className="test-summary">
        {testResults.length > 0 && (
          <div className="summary-stats">
            <span>Всего: {testResults.length}</span>
            <span>✅ Успешно: {testResults.filter(r => r.status === 'success').length}</span>
            <span>❌ Ошибки: {testResults.filter(r => r.status === 'error').length}</span>
            <span>⚠️ Предупреждения: {testResults.filter(r => r.status === 'warning').length}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestPanel; 